import React, { useState } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import "./index.scss";

const AgencyBillProcessing = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedAgencies, setSelectedAgencies] = useState([]);
  const [billPeriod, setBillPeriod] = useState(new Date());
  const [billRunDate, setBillRunDate] = useState(new Date());
  const [billType, setBillType] = useState("Regular");

  const agencies = [
    { id: 1, agencyCode: "AG001", agencyName: "Premier Insurance Agency", agencyType: "Direct", policyCount: 125, grossPremium: 250000, commission: 31250, previousBalance: 5000, totalDue: 223750 },
    { id: 2, agencyCode: "AG002", agencyName: "Global Brokers Ltd", agencyType: "Broker", policyCount: 87, grossPremium: 180000, commission: 27000, previousBalance: -2000, totalDue: 155000 },
    { id: 3, agencyCode: "AG003", agencyName: "Corporate Solutions", agencyType: "Corporate", policyCount: 156, grossPremium: 420000, commission: 42000, previousBalance: 0, totalDue: 378000 },
    { id: 4, agencyCode: "AG004", agencyName: "ABC Bank Insurance", agencyType: "Bancassurance", policyCount: 234, grossPremium: 560000, commission: 47600, previousBalance: 12000, totalDue: 524400 }
  ];

  const agencyBills = [
    { agencyCode: "AG001", agencyName: "Premier Insurance Agency", billNumber: "BIL202509001", billDate: "2025-09-26", dueDate: "2025-10-26", billAmount: 223750, status: "Draft" },
    { agencyCode: "AG002", agencyName: "Global Brokers Ltd", billNumber: "BIL202509002", billDate: "2025-09-26", dueDate: "2025-11-10", billAmount: 155000, status: "Draft" },
    { agencyCode: "AG003", agencyName: "Corporate Solutions", billNumber: "BIL202509003", billDate: "2025-09-26", dueDate: "2025-11-25", billAmount: 378000, status: "Draft" }
  ];

  const adjustments = [
    { agencyName: "Premier Insurance Agency", type: "Credit Note", amount: -5000, reason: "Policy cancellation refund" },
    { agencyName: "Global Brokers Ltd", type: "Additional Charge", amount: 2500, reason: "Late payment charge" }
  ];

  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case 'Draft': return 'warning';
        case 'Generated': return 'success';
        case 'Sent': return 'info';
        default: return null;
      }
    };
    return <Tag value={rowData.status} severity={getSeverity(rowData.status)} />;
  };

  return (
    <div className="agency-bill-processing">
      <h2>Agency Bill Processing</h2>

      <div className="header-section">
        <div className="p-fluid p-formgrid p-grid">
          <div className="p-field p-col-12 p-md-3">
            <label>Bill Period</label>
            <Calendar value={billPeriod} onChange={(e) => setBillPeriod(e.value)} view="month" dateFormat="mm/yy" />
          </div>
          <div className="p-field p-col-12 p-md-3">
            <label>Bill Run Date</label>
            <Calendar value={billRunDate} onChange={(e) => setBillRunDate(e.value)} dateFormat="yy-mm-dd" />
          </div>
          <div className="p-field p-col-12 p-md-3">
            <label>Bill Type</label>
            <Dropdown value={billType} options={[
              { label: "Regular", value: "Regular" },
              { label: "Supplementary", value: "Supplementary" },
              { label: "Adjustment", value: "Adjustment" }
            ]} onChange={(e) => setBillType(e.value)} />
          </div>
          <div className="p-field p-col-12 p-md-3">
            <label>Status</label>
            <Tag value="Draft" severity="warning" style={{ marginTop: '1.5rem' }} />
          </div>
        </div>
      </div>

      <Card className="mt-4">
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header="Select Agencies">
            <div className="filter-section mb-3">
              <Dropdown placeholder="Agency Type" options={[
                { label: "All", value: "All" },
                { label: "Direct", value: "Direct" },
                { label: "Broker", value: "Broker" },
                { label: "Corporate", value: "Corporate" },
                { label: "Bancassurance", value: "Bancassurance" }
              ]} className="mr-2" />
              <Dropdown placeholder="Region" options={[
                { label: "All Regions", value: "All" },
                { label: "North", value: "North" },
                { label: "South", value: "South" },
                { label: "East", value: "East" },
                { label: "West", value: "West" }
              ]} className="mr-2" />
              <Button label="Load Agencies" icon="pi pi-refresh" />
            </div>

            <DataTable
              value={agencies}
              selection={selectedAgencies}
              onSelectionChange={(e) => setSelectedAgencies(e.value)}
              dataKey="id"
              stripedRows
            >
              <Column selectionMode="multiple" style={{ width: '3rem' }} />
              <Column field="agencyCode" header="Agency Code" />
              <Column field="agencyName" header="Agency Name" />
              <Column field="agencyType" header="Type" />
              <Column field="policyCount" header="Policies" />
              <Column field="grossPremium" header="Gross Premium" body={(data) => `$${data.grossPremium.toLocaleString()}`} />
              <Column field="commission" header="Commission" body={(data) => `$${data.commission.toLocaleString()}`} />
              <Column field="previousBalance" header="Previous Balance" body={(data) => `$${data.previousBalance.toLocaleString()}`} />
              <Column field="totalDue" header="Total Due" body={(data) => <strong>${data.totalDue.toLocaleString()}</strong>} />
            </DataTable>
          </TabPanel>

          <TabPanel header="Bill Details">
            <div className="summary-cards mb-4">
              <Card className="summary-card">
                <div className="card-content">
                  <i className="pi pi-building" />
                  <div>
                    <div className="value">{selectedAgencies.length}</div>
                    <div className="label">Total Agencies</div>
                  </div>
                </div>
              </Card>
              <Card className="summary-card">
                <div className="card-content">
                  <i className="pi pi-dollar" />
                  <div>
                    <div className="value">$1,410,000</div>
                    <div className="label">Total Premium</div>
                  </div>
                </div>
              </Card>
              <Card className="summary-card">
                <div className="card-content">
                  <i className="pi pi-percentage" />
                  <div>
                    <div className="value">$147,850</div>
                    <div className="label">Total Commission</div>
                  </div>
                </div>
              </Card>
              <Card className="summary-card highlight">
                <div className="card-content">
                  <i className="pi pi-calculator" />
                  <div>
                    <div className="value">$1,280,650</div>
                    <div className="label">Net Payable</div>
                  </div>
                </div>
              </Card>
            </div>

            <DataTable value={agencyBills} stripedRows>
              <Column field="agencyCode" header="Agency Code" />
              <Column field="agencyName" header="Agency Name" />
              <Column field="billNumber" header="Bill Number" />
              <Column field="billDate" header="Bill Date" />
              <Column field="dueDate" header="Due Date" />
              <Column field="billAmount" header="Bill Amount" body={(data) => `$${data.billAmount.toLocaleString()}`} />
              <Column field="status" header="Status" body={statusBodyTemplate} />
              <Column header="Actions" body={() => (
                <div className="action-buttons">
                  <Button icon="pi pi-eye" className="p-button-rounded p-button-text" tooltip="View" />
                  <Button icon="pi pi-pencil" className="p-button-rounded p-button-text" tooltip="Edit" />
                  <Button icon="pi pi-print" className="p-button-rounded p-button-text" tooltip="Print" />
                </div>
              )} />
            </DataTable>
          </TabPanel>

          <TabPanel header="Adjustments">
            <div className="adjustment-form mb-4">
              <h4>Add Adjustment</h4>
              <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col-12 p-md-6">
                  <label>Agency</label>
                  <Dropdown placeholder="Select Agency" options={selectedAgencies.map(a => ({ label: a.agencyName, value: a.agencyCode }))} />
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Adjustment Type</label>
                  <Dropdown placeholder="Select Type" options={[
                    { label: "Credit Note", value: "Credit Note" },
                    { label: "Debit Note", value: "Debit Note" },
                    { label: "Refund", value: "Refund" },
                    { label: "Additional Charge", value: "Additional Charge" }
                  ]} />
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Amount</label>
                  <InputNumber mode="currency" currency="USD" />
                </div>
                <div className="p-field p-col-12 p-md-6">
                  <label>Reason</label>
                  <InputTextarea rows={2} />
                </div>
              </div>
              <Button label="Add Adjustment" icon="pi pi-plus" className="p-button-secondary" />
            </div>

            <DataTable value={adjustments} stripedRows>
              <Column field="agencyName" header="Agency" />
              <Column field="type" header="Type" />
              <Column field="amount" header="Amount" body={(data) => `$${Math.abs(data.amount).toLocaleString()}`} />
              <Column field="reason" header="Reason" />
              <Column header="" body={() => <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" />} style={{ width: '60px' }} />
            </DataTable>
          </TabPanel>

          <TabPanel header="Finalize">
            <div className="final-summary">
              <Card title="Final Bill Summary">
                <table className="summary-table">
                  <tbody>
                    <tr>
                      <td>Total Gross Premium</td>
                      <td className="text-right">$1,410,000</td>
                    </tr>
                    <tr>
                      <td>Total Commission Earned</td>
                      <td className="text-right">-$147,850</td>
                    </tr>
                    <tr>
                      <td>Service Tax (18%)</td>
                      <td className="text-right">-$26,613</td>
                    </tr>
                    <tr>
                      <td>Previous Balance</td>
                      <td className="text-right">$15,000</td>
                    </tr>
                    <tr>
                      <td>Adjustments</td>
                      <td className="text-right">-$2,500</td>
                    </tr>
                    <tr className="total-row">
                      <td><strong>Net Amount Payable</strong></td>
                      <td className="text-right"><strong>$1,248,037</strong></td>
                    </tr>
                  </tbody>
                </table>
              </Card>

              <div className="processing-options mt-4">
                <h4>Processing Options</h4>
                <div className="p-fluid p-formgrid p-grid">
                  <div className="p-field p-col-12 p-md-6">
                    <div className="checkbox-wrapper">
                      <Checkbox checked={true} />
                      <label>Generate Bills</label>
                    </div>
                  </div>
                  <div className="p-field p-col-12 p-md-6">
                    <div className="checkbox-wrapper">
                      <Checkbox checked={false} />
                      <label>Send to Agencies</label>
                    </div>
                  </div>
                  <div className="p-field p-col-12 p-md-6">
                    <div className="checkbox-wrapper">
                      <Checkbox checked={true} />
                      <label>Create GL Entries</label>
                    </div>
                  </div>
                  <div className="p-field p-col-12 p-md-6">
                    <div className="checkbox-wrapper">
                      <Checkbox checked={false} />
                      <label>Post to Accounts</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabView>

        <div className="action-bar mt-4">
          <Button label="Save Draft" className="p-button-secondary mr-2" />
          <Button label="Validate" icon="pi pi-check" className="p-button-secondary mr-2" />
          <Button label="Process Bills" icon="pi pi-forward" className="p-button-primary mr-2" />
          <Button label="Cancel" className="p-button-text" />
        </div>
      </Card>
    </div>
  );
};

export default AgencyBillProcessing;
